import { GridState } from "ag-grid-enterprise";
import type { ConditionalFormattingRule } from "components/conditional-formatting/types";
import {
  ContextColumnConfigType,
  ContextDatasetConfigType
} from "pages/reports-menu/project-reports/feature-custom-reports/models";

export const PRINT_VIEW_STORAGE_KEY = "printViewKey";

type BasePrintViewState = {
  pageId:
    | "custom-material-report"
    | "material-log"
    | "submittal-log"
    | "bid-package-log"
    | "activity-log"
    | "design-package-planner"
    | "design-scope-log"
    | "design-scope-catalog"
    | "design-scope-linked-scope";
  title: string;
  initialState: GridState;
  context: any;
  rowHeight: number | undefined;
  headerHeight: number | undefined;
  skipColumnIds: string[];
  /** Export-only columns that are hidden in the source grid but must print. */
  forceVisibleColumnIds?: string[];
  /** Print view fetches material comments and shows the Comments column. */
  includeComments?: boolean;
  /** Conditional formatting rules from the source log grid */
  conditionalsFormatting?: ConditionalFormattingRule[];
  /** Planner print: search query to preserve from the card view. */
  query?: string;
};

export type CustomReportPrintViewState = BasePrintViewState & {
  reportId?: string;
  datasetConfig?: ContextDatasetConfigType | null;
  enableCellSpan?: boolean | undefined;
  columnConfig?: ContextColumnConfigType[] | null;
};

export type MaterialLogPrintViewState = BasePrintViewState & {
  testField?: string | undefined;
};

export type SubmittalLogPrintViewState = BasePrintViewState & {
  testField?: string | undefined;
};

export type BidPackageLogPrintViewState = BasePrintViewState & {
  testField?: string | undefined;
};

export type ActivityLogPrintViewState = BasePrintViewState & {
  testField?: string | undefined;
};

export type DesignScopeLogPrintViewState = BasePrintViewState & {
  /** Hierarchy print uses the classification tree; list print is flat. */
  hierarchy?: boolean;
  treeSystem?: "UF" | "MF";
  /**
   * Tab title for this print window. Browsers use it as the suggested
   * Save-as-PDF name. Other logs leave this unset.
   */
  pdfFileBaseName?: string;
};

export type DesignScopeCatalogPrintViewState = BasePrintViewState & {
  /** Required when pageId is design-scope-catalog. */
  catalogId?: string;
  catalogName?: string;
  hierarchy?: boolean;
  treeSystem?: "UF" | "MF";
  pdfFileBaseName?: string;
};

export type DesignScopeLinkedScopePrintViewState = BasePrintViewState & {
  /** Milestone drawer includes LOD + Note; bid drawer does not. */
  includeLodAndNote?: boolean;
  /** When true, print includes a Bid Package column for the expanded rows. */
  groupByBid?: boolean;
  pdfFileBaseName?: string;
};

export type PrintViewLayoutState = CustomReportPrintViewState &
  MaterialLogPrintViewState &
  SubmittalLogPrintViewState &
  BidPackageLogPrintViewState &
  ActivityLogPrintViewState &
  DesignScopeLogPrintViewState &
  DesignScopeCatalogPrintViewState &
  DesignScopeLinkedScopePrintViewState;
